import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Reveal, SectionHead } from "@/components/site/Motion";
import { GEORGIA_PLACES } from "@/data/site";
import { EImg, T } from "@/context/ContentContext";

export function PlacesExplorer({ places = GEORGIA_PLACES, k = "georgia.places" }) {
  const [active, setActive] = useState(0);
  return (
    <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-start">
      <ul className="lg:col-span-6 order-2 lg:order-1 border-t border-cream/15" data-testid="georgia-places-list">
        {places.map((p, i) => (
          <li key={i}>
            <button onMouseEnter={() => setActive(i)} onClick={() => setActive(i)} data-testid={`georgia-place-${p.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className="w-full text-left py-5 sm:py-6 border-b border-cream/15 grid grid-cols-[3rem_1fr_auto] items-baseline gap-4 group">
              <span className="font-mono text-xs text-cream/60">0{i + 1}</span>
              <span>
                <T k={`${k}.${i}.name`} className={`block font-display text-3xl sm:text-5xl font-extrabold tracking-tight transition-colors duration-500 ${active === i ? "text-cream" : "text-cream/35 group-hover:text-cream/70"}`}>{p.name}</T>
                <motion.span initial={false} animate={{ height: active === i ? "auto" : 0, opacity: active === i ? 1 : 0 }}
                  transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }} className="block overflow-hidden">
                  <T k={`${k}.${i}.text`} className="block pt-2 text-cream/80 max-w-md">{p.text}</T>
                </motion.span>
              </span>
              <T k={`${k}.${i}.tag`} className={`eyebrow text-gold transition-opacity ${active === i ? "opacity-100" : "opacity-0"} hidden sm:block`}>{p.tag}</T>
            </button>
          </li>
        ))}
      </ul>
      <div className="lg:col-span-6 order-1 lg:order-2 lg:sticky lg:top-28">
        <div className="relative arch overflow-hidden aspect-[4/5] max-h-[75vh] mx-auto w-full max-w-md lg:max-w-none" data-testid="georgia-place-image">
          {places.map((p, i) => (
            <EImg place="bottom-20 left-1/2 -translate-x-1/2" as={motion.img} k={`${k}.${i}.img`} editable={active === i} key={i} src={p.image} alt={p.name} initial={false}
              animate={{ opacity: active === i ? 1 : 0, scale: active === i ? 1 : 1.12 }}
              transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
              className="absolute inset-0 h-full w-full object-cover" />
          ))}
          <div className="absolute inset-x-0 bottom-0 p-6 bg-gradient-to-t from-wine-deep/80 to-transparent pointer-events-none">
            <p className="font-serif italic text-3xl text-cream"><T k={`${k}.${active}.name`}>{places[active]?.name}</T></p>
          </div>
        </div>
      </div>
    </div>
  );
}

export function GeorgiaSpotlight() {
  return (
    <section id="georgia" className="relative bg-wine text-cream overflow-hidden grain py-24 sm:py-32" data-testid="georgia-usp-section">
      <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8">
        <SectionHead light k="home.georgia" eyebrow="Our speciality · Sakartvelo" title={["Georgia, before", "everyone else gets there."]}>
          <Reveal className="max-w-sm text-cream/80">
            <T k="home.georgia.intro">Mountains, monasteries, Black Sea sunsets and the oldest wine on earth, a short flight from Delhi. It's the trip we know best.</T>
          </Reveal>
        </SectionHead>
        <PlacesExplorer />
        <div className="mt-14 flex flex-wrap gap-x-8 gap-y-3">
          <Link to="/georgia" data-testid="georgia-view-all-link" className="group inline-flex items-center gap-2 text-gold hover:text-cream transition-colors">
            <T k="home.georgia.link1">Explore Georgia trips</T> <ArrowUpRight className="h-4 w-4 transition-transform group-hover:rotate-45" />
          </Link>
          <Link to="/georgia/visa-guide" data-testid="georgia-visa-guide-link" className="group inline-flex items-center gap-2 text-cream/85 hover:text-gold transition-colors">
            <T k="home.georgia.link2">Visa &amp; best time guide</T> <ArrowUpRight className="h-4 w-4 transition-transform group-hover:rotate-45" />
          </Link>
        </div>
      </div>
    </section>
  );
}
